'use client';

import { useState, useCallback } from 'react';
import Link from 'next/link';
import { aiModelsApi, SVDGenerateResponse } from '@/lib/ai-models-api';

export default function SVDPage() {
  const [sourceImage, setSourceImage] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [modelVariant, setModelVariant] = useState('svd-xt');
  const [numFrames, setNumFrames] = useState(25);
  const [fps, setFps] = useState(6);
  const [motionBucketId, setMotionBucketId] = useState(127);
  const [noiseAugStrength, setNoiseAugStrength] = useState(0.02);
  const [decodeChunkSize, setDecodeChunkSize] = useState(8);
  const [steps, setSteps] = useState(25);
  const [minGuidance, setMinGuidance] = useState(1.0);
  const [maxGuidance, setMaxGuidance] = useState(3.0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SVDGenerateResponse | null>(null);

  const handleImageChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const base64 = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.readAsDataURL(file);
    });

    setSourceImage(base64);
    setImagePreview(URL.createObjectURL(file));
    setResult(null);
    setError(null);
  }, []);

  const handleVariantChange = (variant: string) => {
    setModelVariant(variant);
    // Adjust default frames based on variant
    if (variant === 'svd') {
      setNumFrames(14);
    } else {
      setNumFrames(25);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sourceImage) return;

    setLoading(true);
    setError(null);

    try {
      const response = await aiModelsApi.svdGenerate(sourceImage, {
        model_variant: modelVariant,
        num_frames: numFrames,
        fps,
        motion_bucket_id: motionBucketId,
        noise_aug_strength: noiseAugStrength,
        decode_chunk_size: decodeChunkSize,
        num_inference_steps: steps,
        min_guidance_scale: minGuidance,
        max_guidance_scale: maxGuidance,
      });

      setResult(response);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[var(--bg-primary)]">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-[var(--border-subtle)]">
        <div className="max-w-7xl mx-auto px-6 h-12 flex items-center gap-4">
          <Link
            href="/ai-models"
            className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
          </Link>
          <h1 className="text-lg font-semibold">Stable Video Diffusion - Image to Video</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Parameters */}
            <div className="space-y-6">
              <form onSubmit={handleSubmit} className="bg-[var(--bg-elevated)] rounded-xl p-6 space-y-5">
                <h2 className="text-lg font-semibold">Parameters</h2>

                {/* Source Image Upload */}
                <div>
                  <label className="block text-sm font-medium mb-2">Source Image</label>
                  <div className="relative">
                    {imagePreview ? (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Source"
                          className="w-full h-48 object-contain bg-[var(--bg-primary)] rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setSourceImage(null);
                            setImagePreview(null);
                            setResult(null);
                          }}
                          className="absolute top-2 right-2 p-1 bg-black/60 hover:bg-red-500 rounded transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-[var(--border-subtle)] rounded-lg cursor-pointer hover:border-[var(--accent)] transition-colors">
                        <svg className="w-10 h-10 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                        </svg>
                        <span className="mt-2 text-sm text-[var(--text-muted)]">Upload source image</span>
                        <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                      </label>
                    )}
                  </div>
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    This image will be animated into a video
                  </p>
                </div>

                {/* Model Variant */}
                <div>
                  <label className="block text-sm font-medium mb-2">Model Variant</label>
                  <select
                    value={modelVariant}
                    onChange={(e) => handleVariantChange(e.target.value)}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                  >
                    <option value="svd-xt">SVD-XT - 25 frames (~4 seconds)</option>
                    <option value="svd">SVD Base - 14 frames (~2 seconds)</option>
                  </select>
                </div>

                {/* Num Frames */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Frames: {numFrames}
                  </label>
                  <input
                    type="range"
                    min="1"
                    max={modelVariant === 'svd-xt' ? 25 : 14}
                    value={numFrames}
                    onChange={(e) => setNumFrames(parseInt(e.target.value))}
                    className="w-full accent-[var(--accent)]"
                  />
                </div>

                {/* FPS */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    FPS: {fps}
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="30"
                    value={fps}
                    onChange={(e) => setFps(parseInt(e.target.value))}
                    className="w-full accent-[var(--accent)]"
                  />
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    Video duration: ~{(numFrames / fps).toFixed(1)} seconds
                  </p>
                </div>

                {/* Motion Intensity */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Motion Intensity: {motionBucketId}
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="255"
                    value={motionBucketId}
                    onChange={(e) => setMotionBucketId(parseInt(e.target.value))}
                    className="w-full accent-[var(--accent)]"
                  />
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    Higher = more motion, Lower = more stable
                  </p>
                </div>

                {/* Inference Steps */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Steps: {steps}
                  </label>
                  <input
                    type="range"
                    min="10"
                    max="50"
                    value={steps}
                    onChange={(e) => setSteps(parseInt(e.target.value))}
                    className="w-full accent-[var(--accent)]"
                  />
                </div>

                {/* Advanced Settings */}
                <details className="bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg">
                  <summary className="cursor-pointer font-medium p-3">Advanced Settings</summary>
                  <div className="p-3 pt-0 space-y-4">
                    {/* Noise Aug Strength */}
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Noise Aug: {noiseAugStrength.toFixed(2)}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="0.5"
                        step="0.01"
                        value={noiseAugStrength}
                        onChange={(e) => setNoiseAugStrength(parseFloat(e.target.value))}
                        className="w-full accent-[var(--accent)]"
                      />
                    </div>

                    {/* Decode Chunk Size */}
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Decode Chunk Size: {decodeChunkSize}
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="25"
                        value={decodeChunkSize}
                        onChange={(e) => setDecodeChunkSize(parseInt(e.target.value))}
                        className="w-full accent-[var(--accent)]"
                      />
                      <p className="text-xs text-[var(--text-muted)] mt-1">
                        Lower = less VRAM usage
                      </p>
                    </div>

                    {/* Guidance Scale Range */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Min Guidance: {minGuidance.toFixed(1)}
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="10"
                          step="0.1"
                          value={minGuidance}
                          onChange={(e) => setMinGuidance(parseFloat(e.target.value))}
                          className="w-full accent-[var(--accent)]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Max Guidance: {maxGuidance.toFixed(1)}
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="10"
                          step="0.1"
                          value={maxGuidance}
                          onChange={(e) => setMaxGuidance(parseFloat(e.target.value))}
                          className="w-full accent-[var(--accent)]"
                        />
                      </div>
                    </div>
                  </div>
                </details>

                {/* Warning */}
                <div className="text-xs text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
                  <strong>Note:</strong> Video generation requires ~20GB+ VRAM and may take several minutes.
                  Reduce decode_chunk_size if you encounter memory errors.
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={!sourceImage || loading}
                  className="w-full py-3 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Generating Video...' : 'Generate Video'}
                </button>

                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                    {error}
                  </div>
                )}
              </form>
            </div>

            {/* Right: Results */}
            <div className="space-y-6">
              <div className="bg-[var(--bg-elevated)] rounded-xl p-6">
                <h2 className="text-lg font-semibold mb-4">Generated Video</h2>

                {!result && !loading && (
                  <div className="flex flex-col items-center justify-center py-48 text-center">
                    <svg className="w-16 h-16 text-[var(--text-muted)] mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <p className="text-[var(--text-muted)]">Upload an image and generate a video</p>
                  </div>
                )}

                {loading && (
                  <div className="flex flex-col items-center justify-center py-48">
                    <div className="w-12 h-12 border-3 border-[var(--accent)] border-t-transparent rounded-full animate-spin mb-4" />
                    <p className="text-[var(--text-muted)]">Generating video...</p>
                    <p className="text-xs text-[var(--text-muted)] mt-1">This may take several minutes</p>
                  </div>
                )}

                {result && (
                  <div className="space-y-4">
                    {/* Generated Video */}
                    <div className="border border-[var(--border-subtle)] rounded-lg overflow-hidden">
                      <video
                        src={`data:video/mp4;base64,${result.video}`}
                        controls
                        autoPlay
                        loop
                        className="w-full"
                      />
                    </div>

                    {/* Download Button */}
                    <a
                      href={`data:video/mp4;base64,${result.video}`}
                      download="svd_output.mp4"
                      className="block w-full py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors text-center"
                    >
                      Download Video
                    </a>

                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-[var(--bg-primary)] rounded-lg">
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Resolution</p>
                        <p className="text-lg font-semibold">{result.width}x{result.height}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Frames</p>
                        <p className="text-lg font-semibold">{result.num_frames}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">FPS</p>
                        <p className="text-lg font-semibold">{result.fps}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Duration</p>
                        <p className="text-lg font-semibold">{(result.num_frames / result.fps).toFixed(1)}s</p>
                      </div>
                    </div>

                    {/* Additional Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-[var(--bg-primary)] rounded-lg">
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Motion</p>
                        <p className="text-sm font-medium">{result.motion_bucket_id}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Seed</p>
                        <p className="text-sm font-medium">{result.seed}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Model</p>
                        <p className="text-sm font-medium">{result.model_variant}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Time</p>
                        <p className="text-sm font-medium">{(result.inference_time_ms / 1000).toFixed(1)}s</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
